<template>
  <div class="payroll">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <h2 class="title">Bank</h2>
        <app-input
          v-model="search_bank"
          class="input--300"
          type="search"
          placeholder="Search by name"
          name="search"
        />
      </app-cells>
      <v-client-table
        :data="filteredBank"
        :columns="table_bank.columns"
        :options="table_bank.options"
        class="table-default payroll__table-scroll"
      >
        <template #h__name><div class="table-default__left">Full name</div></template>
        <template #name="{ row }">
          <div class="table-default__left table-default__controls payroll__employee">
            <router-link
              v-if="is_general_manager || is_hr_manager || is_hr_supervisor"
              :to=" { name: 'employees-contracts-salaries', params: { id: row.employee_month.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee_month.employee.full_name }}
            </router-link>
            <template v-else>{{ row.employee_month.employee.full_name }}</template>
            <span
              v-if="row.employee_month.employee.employment_status !=='working'"
              class="status-tag"
              style="background-color: #e85c20;"
            >
              Terminated
            </span>
          </div>
        </template>
        <template #basic="{ row }">{{ row.employee_month.basic.toLocaleString() }}</template>
        <template #allowance="{ row }">{{ row.employee_month.allowance.toLocaleString() }}</template>
        <template #penalties="{ row }">
          {{ row.employee_month.total_warnings > 0 ? '-' : '' }}{{ row.employee_month.total_warnings.toLocaleString() }}
        </template>
        <template #bonuses="{ row }">
          <span
            v-tooltip="{ content: `With NHIF: ${row.total_benefits_with_nhif} / No NHIF: ${row.total_benefits_without_nhif}`, placement: 'top-start', offset: 10 }"
          >
            {{ row.employee_month.total_benefits.toLocaleString() }}
          </span>
        </template>
        <template #gross_before_unpaid_leave="{ row }">
          {{ formatAmount(row.gross_before_unpaid_leave) }}
        </template>
        <template #unpaid_leave="{ row }">
          <span
            v-tooltip="unpaidTooltipOptions(row)"
            class="payroll__unpaid-leave"
            tabindex="0"
          >
            {{ unpaidLeaveLabel(row) }}
          </span>
        </template>
        <template #gross="{ row }">{{ row.gross.toLocaleString() }}</template>
        <template #nssf="{ row }">
          <span
            :class="{ 'status-tag': !row.employee_month.employee.nssf_id }"
            :style="!row.employee_month.employee.nssf_id ? 'backgroundColor: #e85c20' : ''"
            v-tooltip="{ content: 'Employee / Employer', placement: 'top-start', offset: 10 }"
          >
            {{ row.nssf_employee.toLocaleString() }} / {{ row.nssf_employer.toLocaleString() }}
          </span>
        </template>
        <template #nhif="{ row }">
          <span
            :class="{ 'status-tag': row.employee_month.employee.nhif_payer && !row.employee_month.employee.nhif_id }"
            :style="row.employee_month.employee.nhif_payer && !row.employee_month.employee.nhif_id ? 'backgroundColor: #e85c20' : ''"
            v-tooltip="{ content: 'Employee / Employer', placement: 'top-start', offset: 10 }"
          >
            {{ row.nhif_employee.toLocaleString() }} / {{ row.nhif_employer.toLocaleString() }}
          </span>
        </template>
        <template #paye="{ row }">
          <span
            :class="{ 'status-tag': !row.employee_month.employee.tin }"
            :style="!row.employee_month.employee.tin ? 'backgroundColor: #e85c20' : ''"
          >
            {{ row.paye.toLocaleString() }}
          </span>
        </template>
        <template #net_salary="{ row }">{{ row.net_salary.toLocaleString() }}</template>
        <template #heslb_loan_amount="{ row }">{{ row.heslb_loan_amount.toLocaleString() }}</template>
        <template #staff_loan_amount="{ row }">{{ row.staff_loan_amount.toLocaleString() }}</template>
        <template #advance_amount="{ row }">{{ row.advance_amount.toLocaleString() }}</template>
        <template #accrued_salary="{ row }">{{ row.accrued_salary.toLocaleString() }}</template>
      </v-client-table>
      <app-cells>
        <h2 class="title">Cash</h2>
        <app-input
          v-model="search_cash"
          class="input--300"
          type="search"
          placeholder="Search by name"
          name="search"
        />
      </app-cells>
      <v-client-table
        :data="filteredCash"
        :columns="table_cash.columns"
        :options="table_cash.options"
        class="table-default payroll__table-scroll"
      >
        <template #h__name><div class="table-default__left">Full name</div></template>
        <template #name="{ row }">
          <div class="table-default__left table-default__controls payroll__employee">
            <router-link
              v-if="is_general_manager || is_hr_manager || is_hr_supervisor"
              :to=" { name: 'employees-contracts-salaries', params: { id: row.employee_month.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee_month.employee.full_name }}
            </router-link>
            <template v-else>{{ row.employee_month.employee.full_name }}</template>
            <span
              v-if="row.employee_month.employee.employment_status !=='working'"
              class="status-tag"
              style="background-color: #e85c20;"
            >
              Terminated
            </span>
          </div>
        </template>
        <template #basic="{ row }">{{ row.employee_month.basic.toLocaleString() }}</template>
        <template #allowance="{ row }">{{ row.employee_month.allowance.toLocaleString() }}</template>
        <template #penalties="{ row }">
          {{ row.employee_month.total_warnings > 0 ? '-' : '' }}{{ row.employee_month.total_warnings.toLocaleString() }}
        </template>
        <template #bonuses="{ row }">
          <span
            v-tooltip="{ content: `With NHIF: ${row.total_benefits_with_nhif} / No NHIF: ${row.total_benefits_without_nhif}`, placement: 'top-start', offset: 10 }"
          >
            {{ row.employee_month.total_benefits.toLocaleString() }}
          </span>
        </template>
        <template #gross_before_unpaid_leave="{ row }">
          {{ formatAmount(row.gross_before_unpaid_leave) }}
        </template>
        <template #unpaid_leave="{ row }">
          <span
            v-tooltip="unpaidTooltipOptions(row)"
            class="payroll__unpaid-leave"
            tabindex="0"
          >
            {{ unpaidLeaveLabel(row) }}
          </span>
        </template>
        <template #gross="{ row }">{{ row.gross.toLocaleString() }}</template>
        <template #nssf="{ row }">
          <span
            :class="{ 'status-tag': !row.employee_month.employee.nssf_id }"
            :style="!row.employee_month.employee.nssf_id ? 'backgroundColor: #e85c20' : ''"
            v-tooltip="{ content: 'Employee / Employer', placement: 'top-start', offset: 10 }"
          >
            {{ row.nssf_employee.toLocaleString() }} / {{ row.nssf_employer.toLocaleString() }}
          </span>
        </template>
        <template #nhif="{ row }">
          <span
            :class="{ 'status-tag': row.employee_month.employee.nhif_payer && !row.employee_month.employee.nhif_id }"
            :style="row.employee_month.employee.nhif_payer && !row.employee_month.employee.nhif_id ? 'backgroundColor: #e85c20' : ''"
            v-tooltip="{ content: 'Employee / Employer', placement: 'top-start', offset: 10 }"
          >
            {{ row.nhif_employee.toLocaleString() }} / {{ row.nhif_employer.toLocaleString() }}
          </span>
        </template>
        <template #paye="{ row }">
          <span
            :class="{ 'status-tag': !row.employee_month.employee.tin }"
            :style="!row.employee_month.employee.tin ? 'backgroundColor: #e85c20' : ''"
          >
            {{ row.paye.toLocaleString() }}
          </span>
        </template>
        <template #wht="{ row }">{{ row.wht.toLocaleString() }} TSh</template>
        <template #net_salary="{ row }">{{ row.net_salary.toLocaleString() }}</template>
        <template #heslb_loan_amount="{ row }">{{ row.heslb_loan_amount.toLocaleString() }}</template>
        <template #staff_loan_amount="{ row }">{{ row.staff_loan_amount.toLocaleString() }}</template>
        <template #advance_amount="{ row }">{{ row.advance_amount.toLocaleString() }}</template>
        <template #accrued_salary="{ row }">{{ row.accrued_salary.toLocaleString() }}</template>
      </v-client-table>
    </template>
  </div>
</template>

<script>
import { format, isValid, parseISO } from 'date-fns'
import { getEmployeesPayrollEmployees } from '@/http'

export default {
  name: 'EmployeesPayrollEmployees',
  props: {
    payroll: Object,
  },
  data() {
    return {
      is_loaded: false,
      table_bank: {
        items: [],
        columns: [
          'name',
          'basic',
          'allowance',
          'penalties',
          'bonuses',
          'gross_before_unpaid_leave',
          'unpaid_leave',
          'gross',
          'nssf',
          'nhif',
          'paye',
          'wht',
          'net_salary',
          'heslb_loan_amount',
          'staff_loan_amount',
          'advance_amount',
          'accrued_salary',
        ],
        options: {
          headings: {
            name: 'Full name',
            basic: 'Basic',
            allowance: 'Allowance',
            penalties: 'Penalties',
            bonuses: 'Benefits',
            gross_before_unpaid_leave: 'Gross before unpaid',
            unpaid_leave: 'Unpaid leave',
            gross: 'Adjusted gross',
            nssf: 'NSSF',
            nhif: 'NHIF',
            paye: 'PAYE',
            wht: 'WHT',
            net_salary: 'Net salary',
            heslb_loan_amount: 'HESLB Loan',
            staff_loan_amount: 'Staff Loan',
            advance_amount: 'Advances',
            accrued_salary: 'Accrued salary',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 500,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_cash: {
        items: [],
        columns: [
          'name',
          'basic',
          'allowance',
          'penalties',
          'bonuses',
          'gross_before_unpaid_leave',
          'unpaid_leave',
          'gross',
          'nssf',
          'nhif',
          'paye',
          'wht',
          'net_salary',
          'heslb_loan_amount',
          'staff_loan_amount',
          'advance_amount',
          'accrued_salary',
        ],
        options: {
          headings: {
            name: 'Full name',
            basic: 'Basic',
            allowance: 'Allowance',
            penalties: 'Penalties',
            bonuses: 'Benefits',
            gross_before_unpaid_leave: 'Gross before unpaid',
            unpaid_leave: 'Unpaid leave',
            gross: 'Adjusted gross',
            nssf: 'NSSF',
            nhif: 'NHIF',
            paye: 'PAYE',
            wht: 'WHT',
            net_salary: 'Net salary',
            heslb_loan_amount: 'HESLB Loan',
            staff_loan_amount: 'Staff Loan',
            advance_amount: 'Advances',
            accrued_salary: 'Accrued salary',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 500,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      search_bank: '',
      search_cash: '',
      requestId: 0,
    }
  },
  watch: {
    payroll() {
      this.fetchItems()
    }
  },
  created() {
    this.fetchItems()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_hr_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('hr_manager')
    },
    is_hr_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('hr_supervisor')
    },
    filteredBank() {
      return this.table_bank.items.filter(item => {
        return this.search_bank ? item.employee_month.employee.full_name.toLowerCase().includes(this.search_bank.toLowerCase()) : this.table_bank.items
      })
    },
    filteredCash() {
      return this.table_cash.items.filter(item => {
        return this.search_cash ? item.employee_month.employee.full_name.toLowerCase().includes(this.search_cash.toLowerCase()) : this.table_cash.items
      })
    },
  },
  methods: {
    valuePresent(value) {
      return value !== null && value !== undefined && value !== ''
    },
    formatAmount(value) {
      if (!this.valuePresent(value)) return '-'
      const amount = Number(value)
      return Number.isFinite(amount) ? amount.toLocaleString() : String(value)
    },
    formatDays(value) {
      if (!this.valuePresent(value)) return '-'
      const days = Number(value)
      const label = Number.isFinite(days) ? days.toLocaleString() : String(value)
      return `${label} ${days === 1 ? 'day' : 'days'}`
    },
    formatDate(value) {
      if (!value) return '-'
      const date = parseISO(value)
      return isValid(date) ? format(date, 'dd MMM yyyy') : value
    },
    currencyLabel(value) {
      const amount = this.formatAmount(value)
      return amount === '-' ? amount : `${amount} TSh`
    },
    unpaidLeaveLabel(row) {
      const days = this.formatDays(row.unpaid_leave_days)
      const amount = this.formatAmount(row.unpaid_leave_deduction)
      if (days === '-' && amount === '-') return '-'
      return `${days} / ${amount === '-' ? '-' : `-${amount}`}`
    },
    unpaidTooltipOptions(row) {
      return {
        content: this.unpaidTooltip(row),
        placement: 'top-start',
        offset: 10,
        classes: 'tooltip-payroll-unpaid',
      }
    },
    unpaidTooltip(row) {
      const sources = Array.isArray(row.unpaid_leave_breakdown) ? row.unpaid_leave_breakdown : []
      const lines = [
        `Basic base: ${this.currencyLabel(row.unpaid_leave_basic_base)}`,
        `Calendar days: ${this.formatDays(row.unpaid_leave_days)}`,
        `Rounded deduction: ${this.currencyLabel(row.unpaid_leave_deduction)}`,
        sources.length ? 'Source periods:' : 'Source periods: -',
      ]

      sources.forEach(item => {
        const fromDate = item.eligible_from_date || item.from_date
        const toDate = item.eligible_to_date || item.to_date
        lines.push(`• ${this.formatDate(fromDate)} – ${this.formatDate(toDate)}: ${this.formatDays(item.calendar_days)}`)
      })

      return lines.join('\n')
    },
    fetchItems() {
      const requestId = ++this.requestId
      this.is_loaded = false
      return Promise.all([
        getEmployeesPayrollEmployees({ payroll: this.$route.params.id, payment_method: 'bank' }),
        getEmployeesPayrollEmployees({ payroll: this.$route.params.id, payment_method: 'cash' }),
      ])
        .then(([bankResponse, cashResponse]) => {
          if (requestId !== this.requestId) return
          this.table_bank.items = Array.isArray(bankResponse.data) ? bankResponse.data : []
          this.table_cash.items = Array.isArray(cashResponse.data) ? cashResponse.data : []
        })
        .catch(() => {
          if (requestId !== this.requestId) return
          this.table_bank.items = []
          this.table_cash.items = []
          this.$notify({
            type: 'error',
            title: 'Payroll employees could not be loaded',
          })
        })
        .finally(() => {
          if (requestId === this.requestId) this.is_loaded = true
        })
    },
  }
}
</script>

<style lang="sass" scoped>
.payroll
  position: relative
  width: 100%
  max-width: 100%
  min-width: 0
  min-height: 300px

.payroll__table-scroll
  width: 100%
  max-width: 100%
  overflow-x: auto
  overscroll-behavior-inline: contain

.payroll__employee
  align-items: flex-start
  flex-direction: column
  gap: 4px
</style>

<style lang="sass">
.tooltip.tooltip-payroll-unpaid
  .tooltip-inner
    white-space: pre-line
</style>
